'use client';

import { useRef } from 'react';

import { motion } from 'framer-motion';
import { CalendarCheck, ChevronRight, Clock3, Layers } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import {
  APP_TABS,
  AnimatedNumber,
  AppTabBar,
  Avatar,
  BrandMark,
  CheckBadge,
  DEMO_BRANDS,
  DEMO_COLORS,
  type DemoProps,
  DemoStage,
  EASE_OUT_EXPO,
  PhoneFrame,
  Reveal,
  SPRING_SNAPPY,
  TapRipple,
  localize,
  useDemoTimeline,
} from './demo-kit';

/*
 * Member app, boutique studio: browse today's schedule, open a class, book it
 * with a package credit, then get promoted off the waitlist for a full class.
 */

const DURATIONS = [1500, 900, 2000, 550, 2600, 3400] as const;

const STEP = {
  tapClass: 1,
  sheet: 2,
  pressBook: 3,
  booked: 4,
  promoted: 5,
} as const;

const COPY = {
  label: {
    en: 'Animated demo of the branded studio app: a member books a reformer class with a package credit and is automatically moved off the waitlist for a full class.',
    ar: 'عرض متحرك لتطبيق الاستوديو بعلامته: عضوة تحجز حصة ريفورمر برصيد باقتها، ثم تنتقل تلقائيًا من قائمة الانتظار إلى حصة كانت مكتملة.',
  },
  title: { en: 'Book a class', ar: 'احجز حصة' },
  subtitle: { en: 'Today · Tue 10', ar: 'اليوم · الثلاثاء 10' },
  schedule: { en: 'Schedule', ar: 'الجدول' },
  spotsLeft: { en: 'spots left', ar: 'أماكن متبقية' },
  full: { en: 'Full · Waitlist', ar: 'مكتملة · انتظار' },
  booked: { en: 'Booked', ar: 'محجوزة' },
  sheetWhen: { en: 'Tue 10 · 7:00 to 7:50 AM', ar: 'الثلاثاء 10 · 7:00 إلى 7:50 ص' },
  instructorRole: { en: 'Senior instructor', ar: 'مدربة أولى' },
  minutes: { en: '50 min', ar: '50 دقيقة' },
  level: { en: 'All levels', ar: 'جميع المستويات' },
  spotsOf: { en: '2 of 12 spots left', ar: 'متبقٍ مكانان من 12' },
  credit: { en: 'Uses 1 credit · 7 left', ar: 'تُخصم حصة واحدة من باقتك · يتبقى 7' },
  outOf: { en: 'of 10', ar: 'من 10' },
  bookClass: { en: 'Book class', ar: 'احجز الحصة' },
  viewBooking: { en: 'View in my bookings', ar: 'عرض في حجوزاتي' },
  seeYou: { en: 'See you at 7:00 AM', ar: 'نراكِ الساعة 7:00 ص' },
  withNoor: { en: 'Reformer Flow with Noor', ar: 'ريفورمر فلو مع نور' },
  creditsLeft: { en: 'Credits left in your package', ar: 'الرصيد المتبقي في باقتك' },
  calendar: { en: 'Added to your calendar', ar: 'أُضيفت إلى تقويمك' },
  notifApp: { en: 'Linen Studio', ar: 'استوديو لينن' },
  notifNow: { en: 'now', ar: 'الآن' },
  notifTitle: { en: "You're off the waitlist", ar: 'انتقلتِ من قائمة الانتظار' },
  notifBody: {
    en: 'Mat Pilates 8:30 AM is confirmed',
    ar: 'تم تأكيد حصة بيلاتس مات 8:30 ص',
  },
  ...APP_TABS,
} as const;

const DAYS = [
  { en: 'Mon', ar: 'اثنين', date: 9 },
  { en: 'Tue', ar: 'ثلاثاء', date: 10 },
  { en: 'Wed', ar: 'أربعاء', date: 11 },
  { en: 'Thu', ar: 'خميس', date: 12 },
  { en: 'Fri', ar: 'جمعة', date: 13 },
  { en: 'Sat', ar: 'سبت', date: 14 },
  { en: 'Sun', ar: 'أحد', date: 15 },
] as const;

type ClassStatus = 'spots' | 'waitlist';

const CLASSES: readonly {
  name: { en: string; ar: string };
  time: { en: string; ar: string };
  coach: { en: string; ar: string };
  status: ClassStatus;
  spots?: number;
}[] = [
  {
    name: { en: 'Reformer Flow', ar: 'ريفورمر فلو' },
    time: { en: '7:00 AM', ar: '7:00 ص' },
    coach: { en: 'with Noor', ar: 'مع نور' },
    status: 'spots',
    spots: 2,
  },
  {
    name: { en: 'Mat Pilates', ar: 'بيلاتس مات' },
    time: { en: '8:30 AM', ar: '8:30 ص' },
    coach: { en: 'with Dana', ar: 'مع دانة' },
    status: 'waitlist',
  },
  {
    name: { en: 'Barre', ar: 'بار' },
    time: { en: '6:00 PM', ar: '6:00 م' },
    coach: { en: 'with Layla', ar: 'مع ليلى' },
    status: 'spots',
    spots: 5,
  },
  {
    name: { en: 'Reformer Sculpt', ar: 'ريفورمر سكَلبت' },
    time: { en: '7:30 PM', ar: '7:30 م' },
    coach: { en: 'with Noor', ar: 'مع نور' },
    status: 'spots',
    spots: 3,
  },
];

const TOTAL_SPOTS = 12;

export function ClassBookingDemo({ locale, className, onLoop }: DemoProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const timeline = useDemoTimeline(stageRef, DURATIONS, { onLoop });
  const copy = localize(COPY, locale);
  const brand = DEMO_BRANDS.studio;
  const step = timeline.step;

  const sheetOpen = step === STEP.sheet || step === STEP.pressBook;
  const reformerBooked = step >= STEP.promoted;
  const promoted = step >= STEP.promoted;

  return (
    <DemoStage ref={stageRef} timeline={timeline} label={copy.label} className={className}>
      <PhoneFrame>
        <div className="absolute inset-0 flex flex-col bg-[#fbfaf8] pt-[44px]">
          <div className="flex items-center justify-between px-5 pb-3 pt-2">
            <div className="flex items-center gap-2.5">
              <BrandMark brand={brand} size={28} />
              <span className="text-[13px] font-semibold text-[#202020]">
                {brand.name[locale]}
              </span>
            </div>
            <Avatar initials="SA" tone={3} size={28} />
          </div>

          <div className="px-5">
            <h3 className="text-[21px] font-bold leading-tight text-[#202020]">
              {copy.title}
            </h3>
            <p className="mt-0.5 text-[12px] text-[#8a857c]">{copy.subtitle}</p>
          </div>

          <div className="mt-4 flex justify-between px-4">
            {DAYS.map((day, index) => {
              const active = index === 1;

              return (
                <div
                  key={day.date}
                  className="flex h-[54px] w-[33px] flex-col items-center justify-center rounded-[12px]"
                  style={{ background: active ? brand.accent : 'transparent' }}
                >
                  <span className={cn('text-[8.5px]', active ? 'text-white/80' : 'text-[#8a857c]')}>
                    {day[locale]}
                  </span>
                  <span
                    className={cn(
                      'mt-0.5 text-[14px] font-semibold',
                      active ? 'text-white' : 'text-[#202020]',
                    )}
                  >
                    {day.date}
                  </span>
                </div>
              );
            })}
          </div>

          <p className="mt-4 px-5 font-roboto-mono text-[9.5px] uppercase tracking-[0.12em] text-[#8a857c] rtl:tracking-normal">
            {copy.schedule}
          </p>

          <div className="mt-2 space-y-2 px-4">
            {CLASSES.map((item, index) => {
              const tapped = index === 0 && step >= STEP.tapClass && step < STEP.promoted;
              const isBooked =
                (index === 0 && reformerBooked) || (index === 1 && promoted);

              return (
                <motion.div
                  key={item.name.en}
                  className="relative flex h-[56px] items-center gap-2.5 overflow-hidden rounded-[14px] border bg-white px-3"
                  initial={false}
                  animate={{
                    borderColor: tapped ? brand.accent : DEMO_COLORS.line,
                    backgroundColor: tapped ? brand.accentSoft : '#ffffff',
                    scale: tapped && step === STEP.tapClass ? [1, 0.97, 1] : 1,
                  }}
                  transition={{ duration: 0.35 }}
                >
                  <div className="w-[44px] shrink-0">
                    <p className="whitespace-nowrap text-[12px] font-semibold text-[#202020]">
                      {item.time[locale]}
                    </p>
                    <p className="text-[9px] text-[#8a857c]">{copy.minutes}</p>
                  </div>
                  <span className="h-8 w-[3px] shrink-0 rounded-full" style={{ background: brand.accent }} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12.5px] font-semibold text-[#202020]">
                      {item.name[locale]}
                    </p>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <span className="whitespace-nowrap text-[10px] text-[#8a857c]">
                        {item.coach[locale]}
                      </span>
                      <StatusChip
                        booked={isBooked}
                        bookedLabel={copy.booked}
                        delay={index === 1 ? 1.3 : 0.2}
                        label={
                          item.status === 'waitlist'
                            ? copy.full
                            : `${item.spots} ${copy.spotsLeft}`
                        }
                        tone={
                          item.status === 'waitlist'
                            ? 'waitlist'
                            : item.spots !== undefined && item.spots <= 2
                              ? 'low'
                              : 'normal'
                        }
                        accent={brand.accent}
                        accentSoft={brand.accentSoft}
                      />
                    </div>
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[#b5b0a6] rtl:rotate-180" />
                  <TapRipple
                    key={`ripple-${timeline.cycle}`}
                    show={index === 0 && step === STEP.tapClass}
                    className="left-1/2 top-1/2"
                  />
                </motion.div>
              );
            })}
          </div>

          <AppTabBar
            active={1}
            accent={brand.accent}
            labels={[copy.home, copy.book, copy.bookings, copy.profile]}
          />
        </div>

        {/* Class sheet */}
        <motion.div
          className="absolute inset-0 z-10 bg-black/25"
          initial={false}
          animate={{ opacity: sheetOpen ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        />
        <motion.div
          className="absolute inset-x-0 bottom-0 z-20 rounded-t-[26px] bg-white px-5 pb-8 pt-3"
          initial={false}
          animate={{ y: sheetOpen ? 0 : '105%' }}
          transition={{ duration: 0.55, ease: EASE_OUT_EXPO }}
        >
          <div className="mx-auto mb-4 h-1 w-9 rounded-full bg-[#dcd7cf]" />
          <p className="text-[17px] font-bold text-[#202020]">{CLASSES[0]!.name[locale]}</p>
          <p className="mt-0.5 text-[12px] text-[#8a857c]">{copy.sheetWhen}</p>

          <div className="mt-3.5 flex items-center gap-2.5">
            <Avatar initials={locale === 'ar' ? 'ن' : 'N'} tone={3} size={34} />
            <div>
              <p className="text-[12.5px] font-semibold text-[#202020]">{CLASSES[0]!.coach[locale]}</p>
              <p className="text-[10.5px] text-[#8a857c]">{copy.instructorRole}</p>
            </div>
          </div>

          <div className="mt-3.5 flex flex-wrap gap-1.5">
            <SheetChip icon={<Clock3 className="h-3 w-3" />} label={copy.minutes} />
            <SheetChip icon={<Layers className="h-3 w-3" />} label={copy.level} />
          </div>

          <div className="mt-4 rounded-[14px] border border-[#ebe7e1] p-3">
            <div className="flex gap-[5px]" dir="ltr">
              {Array.from({ length: TOTAL_SPOTS }, (_, index) => {
                const open = index >= TOTAL_SPOTS - 2;

                return (
                  <motion.span
                    key={index}
                    className="h-2 flex-1 rounded-full"
                    initial={false}
                    animate={{
                      backgroundColor: open ? brand.accent : DEMO_COLORS.smoke,
                      opacity: sheetOpen ? 1 : 0,
                      scaleY: sheetOpen ? 1 : 0.3,
                    }}
                    transition={{ duration: 0.3, delay: sheetOpen ? 0.25 + index * 0.03 : 0 }}
                  />
                );
              })}
            </div>
            <p className="mt-2 text-[11px] font-medium text-[#202020]">{copy.spotsOf}</p>
            <p className="mt-0.5 text-[10.5px] text-[#8a857c]">{copy.credit}</p>
          </div>

          <motion.div
            className="relative mt-4 flex h-11 items-center justify-center overflow-hidden rounded-[14px] text-[13px] font-semibold text-white"
            style={{ background: brand.accent }}
            initial={false}
            animate={{ scale: step === STEP.pressBook ? [1, 0.96, 1] : 1 }}
            transition={{ duration: 0.35 }}
          >
            {copy.bookClass}
            <TapRipple
              key={`book-${timeline.cycle}`}
              show={step === STEP.pressBook}
              className="left-1/2 top-1/2"
              color="rgba(255,255,255,0.35)"
            />
          </motion.div>
        </motion.div>

        {/* Confirmation */}
        <motion.div
          className="absolute inset-0 z-40 flex flex-col items-center bg-[#fbfaf8] px-5 pt-[78px]"
          initial={false}
          animate={{
            opacity: step === STEP.booked ? 1 : 0,
            scale: step === STEP.booked ? 1 : 1.04,
          }}
          style={{ pointerEvents: 'none' }}
          transition={{ duration: 0.45, ease: EASE_OUT_EXPO }}
        >
          <CheckBadge show={step === STEP.booked} size={52} bg={brand.accent} delay={0.15} />
          <Reveal show={step === STEP.booked} delay={0.25} className="mt-3 text-center">
            <p className="text-[20px] font-bold text-[#202020]">{copy.seeYou}</p>
            <p className="mt-1 text-[11.5px] text-[#8a857c]">{copy.withNoor}</p>
          </Reveal>

          <Reveal
            show={step === STEP.booked}
            delay={0.4}
            y={18}
            className="mt-6 w-full rounded-[20px] border border-[#ebe7e1] bg-white p-4"
          >
            <p className="text-[10.5px] text-[#8a857c]">{copy.creditsLeft}</p>
            <div className="mt-1 flex items-end justify-between">
              <span className="text-[34px] font-bold leading-none" style={{ color: brand.accent }}>
                <AnimatedNumber value={step >= STEP.booked ? 7 : 8} duration={1.2} />
              </span>
              <span className="mb-1 text-[11px] font-medium text-[#8a857c]">{copy.outOf}</span>
            </div>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#ede8e2]">
              <motion.span
                className="block h-full rounded-full"
                style={{ background: brand.accent }}
                initial={false}
                animate={{ width: step >= STEP.booked ? '70%' : '80%' }}
                transition={{ duration: 1.2, ease: EASE_OUT_EXPO, delay: 0.5 }}
              />
            </div>
          </Reveal>

          <Reveal
            show={step === STEP.booked}
            delay={0.65}
            className="mt-3 flex w-full items-center gap-2.5 rounded-[14px] bg-white px-3 py-2.5 ring-1 ring-[#ebe7e1]"
          >
            <span
              className="flex h-7 w-7 items-center justify-center rounded-full"
              style={{ background: brand.accentSoft, color: brand.accent }}
            >
              <CalendarCheck className="h-3.5 w-3.5" />
            </span>
            <span className="flex-1 text-[11px] font-medium text-[#202020]">{copy.calendar}</span>
            <CheckBadge show={step === STEP.booked} size={18} delay={0.9} />
          </Reveal>

          <Reveal
            show={step === STEP.booked}
            delay={0.8}
            className="mb-9 mt-auto flex h-10 w-full items-center justify-center rounded-[14px] border border-[#dcd7cf] bg-white text-[12px] font-semibold"
          >
            <span style={{ color: brand.accent }}>{copy.viewBooking}</span>
          </Reveal>
        </motion.div>

        {/* Waitlist promotion notification */}
        <motion.div
          className="absolute inset-x-2.5 top-[46px] z-50 rounded-[20px] bg-white/95 p-3 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.35)] ring-1 ring-black/5 backdrop-blur"
          initial={false}
          animate={{ y: promoted ? 0 : -130, opacity: promoted ? 1 : 0 }}
          transition={{ ...SPRING_SNAPPY, delay: promoted ? 0.5 : 0 }}
        >
          <div className="flex items-center gap-2">
            <BrandMark brand={brand} size={18} />
            <span className="flex-1 text-[10px] font-medium uppercase tracking-wide text-[#8a857c] rtl:tracking-normal">
              {copy.notifApp}
            </span>
            <span className="text-[9.5px] text-[#8a857c]">{copy.notifNow}</span>
          </div>
          <p className="mt-1.5 text-[12.5px] font-semibold text-[#202020]">{copy.notifTitle}</p>
          <p className="text-[11px] text-[#5c554b]">{copy.notifBody}</p>
        </motion.div>
      </PhoneFrame>
    </DemoStage>
  );
}

function StatusChip({
  booked,
  bookedLabel,
  label,
  tone,
  accent,
  accentSoft,
  delay,
}: {
  booked: boolean;
  bookedLabel: string;
  label: string;
  tone: 'normal' | 'low' | 'waitlist';
  accent: string;
  accentSoft: string;
  delay: number;
}) {
  const idle = {
    normal: { bg: DEMO_COLORS.bone, fg: '#5c554b' },
    low: { bg: DEMO_COLORS.warningBg, fg: DEMO_COLORS.warning },
    waitlist: { bg: DEMO_COLORS.sand, fg: '#5c554b' },
  }[tone];

  return (
    <motion.span
      className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-[3px] text-[9px] font-medium"
      initial={false}
      animate={{
        backgroundColor: booked ? accentSoft : idle.bg,
        color: booked ? accent : idle.fg,
      }}
      transition={{ duration: 0.3, delay: booked ? delay : 0 }}
    >
      {booked ? (
        <>
          <CheckBadge show size={11} bg={accent} delay={delay} />
          {bookedLabel}
        </>
      ) : (
        label
      )}
    </motion.span>
  );
}

function SheetChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#f7f6f4] px-2.5 py-1 text-[10px] font-medium text-[#5c554b]">
      {icon}
      {label}
    </span>
  );
}
